import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Link from '@material-ui/core/Link';

import { fractionToPercent, formatNumber } from 'helpers/helpers';
import useStyles from './otherStyles';
import { ClassesAsset } from 'common/interfaces';
import { AssetChartStore } from 'stores/workflowStores/newWorkflowPlanStore/assetChartStore';
import AssetChart from './AssetChart';

interface Props {
  asset: ClassesAsset;
  chartStore: AssetChartStore;
  active: boolean;
  savingIsins?: string[];
}

const AssetDetail: React.FC<Props> = ({ asset, chartStore, active, savingIsins }) => {
  const cssClasses = useStyles();
  const { t } = useTranslation();

  return asset.swaps ? (
    <Table className={cssClasses.swapDetail}>
      <TableHead>
        <TableRow>
          <TableCell className={cssClasses.swapDetailTitle}>{t('general_title')}</TableCell>
          <TableCell>{t('accountPlan_swapProfit')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {asset.swaps.map((swap) => (
          <TableRow key={swap.title}>
            <TableCell className={cssClasses.swapDetailTitle}>{swap.title}</TableCell>
            <TableCell>
              {swap.current.profit !== undefined
                ? `${formatNumber(swap.current.profit, 2)} ${swap.current.currency}`
                : ''}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  ) : (
    <Table>
      <TableBody>
        {asset.originalTitle ? (
          <TableRow className={cssClasses.assetDetailRow}>
            <TableCell colSpan={5} className="_assetTitle">
              <strong>{asset.originalTitle}</strong>
            </TableCell>
          </TableRow>
        ) : null}

        <TableRow className={`${cssClasses.assetDetailRow} ${cssClasses.assetDetails}`}>
          <TableCell>
            {t('general_currency')}: {asset.currency}
          </TableCell>
          <TableCell>
            {savingIsins?.includes(asset.isin) ? (
              ''
            ) : (
              <>
                {t('accountPlan_portfolioTable_ISIN')}: {asset.isin}
              </>
            )}
          </TableCell>
          <TableCell>
            {asset.risk && (
              <>
                {t('accountPlan_portfolioTable_risk')}: {asset.risk}
              </>
            )}
          </TableCell>
          <TableCell>
            {t('esg_rating')}: {asset.esgScore !== undefined ? formatNumber(asset.esgScore, 2) : ''}
          </TableCell>
          <TableCell>
            {typeof asset.ter === 'number' &&
              `${t('accountPlan_portfolioTable_TER')}: ${fractionToPercent(asset.ter, 2)}`}
          </TableCell>
        </TableRow>

        {!!asset.description && (
          <TableRow className={cssClasses.assetDescription}>
            <TableCell colSpan={5}>{t(asset.description)}</TableCell>
          </TableRow>
        )}

        <TableRow className={cssClasses.assetDescription}>
          <TableCell colSpan={5}>
            {active && asset.woodId && (
              <AssetChart store={chartStore} woodId={asset.woodId} currency={asset.currency} />
            )}
          </TableCell>
        </TableRow>

        {(asset.kid || asset.report || asset.link) && (
          <TableRow className={`${cssClasses.assetDetailRow} ${cssClasses.assetLinks}`}>
            <TableCell colSpan={5}>
              {asset.kid ? (
                <Link target="_blank" href={asset.kid}>
                  {t('accountPlan_portfolioTable_KIID')}
                </Link>
              ) : (
                <span />
              )}
              {asset.report ? (
                <Link target="_blank" href={asset.report}>
                  {t('accountPlan_portfolioTable_monthReport')}
                </Link>
              ) : (
                <span />
              )}
              {asset.link ? (
                <Link target="_blank" href={asset.link}>
                  {t('accountPlan_morningstarLink')}
                </Link>
              ) : (
                <span />
              )}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default observer(AssetDetail);
